import React, { useState, useRef, useEffect, useContext } from "react";
import ss from "../assets/images/shersoftnavLogo.png";
import "../styles/LoginPage.css";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useDbContext } from "../context/DbContext";
import { useDispatch } from "react-redux";
import { setMenuPermissions } from "../app/permissionsSlice";
import { setSoftwareSettings } from "../app/settingsSlice";
import { flattenMenuTree } from "../utils/flattenMenuTree";
import { setActiveLocation } from "../app/locationSlice";
import { fetchActiveFinancialYear } from "../app/financialYearSlice";

function LoginPage() {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dbDetails = location.state ? location.state.selectedRowData : "";
  const storedDbCode = localStorage.getItem("dbCode");
  const activeDbCode = dbDetails?.Code || storedDbCode || "";

  const { login, logout } = useContext(AuthContext);
  const { setDbCode } = useDbContext();

  // Helper for today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const [locationOptions, setLocationOptions] = useState([]);
  const [rawLocations, setRawLocations] = useState([]);

  const [loginData, setLoginData] = useState({
    userName: "",
    password: "",
    location: localStorage.getItem("loginLocation") || sessionStorage.getItem("loginLocation") || "SHOP",
    loginDate: getTodayDate(),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch registered Location options for selected activeDbCode directly from OtherRegistration
  useEffect(() => {
    if (!activeDbCode) return;
    axios
      .get(`${apiBaseUrl}/otherregistration/getoptions/${activeDbCode}`)
      .then((res) => {
        const records = res.data || [];
        const locationRecords = records.filter(
          (r) => String(r.Type || r.type || "").toLowerCase() === "location"
        );

        setRawLocations(locationRecords);

        const locations = locationRecords
          .map((r) => (r.Name || r.name || "").trim())
          .filter(Boolean);

        if (locations.length > 0) {
          setLocationOptions(locations);
          const savedLoc = localStorage.getItem("loginLocation") || sessionStorage.getItem("loginLocation") || "";
          const defaultLoc = locations.find((l) => l.trim().toLowerCase() === savedLoc.trim().toLowerCase()) || locations[0];
          setLoginData((prev) => ({
            ...prev,
            location: defaultLoc,
          }));
        } else {
          setLocationOptions(["SHOP"]);
        }
      })
      .catch((err) => {
        console.error("Error fetching locations for active DB:", activeDbCode, err);
        setLocationOptions(["SHOP"]);
      });
  }, [activeDbCode, apiBaseUrl]);

  const userNameRef = useRef(null);
  const passwordRef = useRef(null);
  const locationRef = useRef(null);
  const dateRef = useRef(null);
  const loginBtnRef = useRef(null);

  useEffect(() => {
    if (userNameRef.current) {
      userNameRef.current.focus();
    }
  }, [dbDetails]);

  const handleKeyDown = (event, nextInputRef) => {
    if (event.key === "Enter" || event.key === "Return") {
      event.preventDefault();
      if (nextInputRef && nextInputRef.current) {
        nextInputRef.current.focus();
      }
    }
  };

  const handleLogin = async () => {
    if (!activeDbCode) {
      alert("Please select a company database first.");
      navigate("/");
      return;
    }

    if (!loginData.userName.trim()) {
      setError("Please enter your username.");
      userNameRef.current?.focus();
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${apiBaseUrl}/main/userLogin`, {
        userName: loginData.userName,
        password: loginData.password,
        dbName: activeDbCode,
      });

      const { agentCode, userType, customer, uRights, userID, userRegisterId, token } =
        response.data;

      let selectedLocationId = 0;
      let selectedLocationName = loginData.location || "SHOP";
      let selectedLocationObj = rawLocations.find(
        (b) => (b.Name || b.name || "").trim().toLowerCase() === (loginData.location || "").trim().toLowerCase()
      );
      if (selectedLocationObj) {
        selectedLocationId = Number(selectedLocationObj.Auto ?? selectedLocationObj.auto ?? 0);
        selectedLocationName = selectedLocationObj.Name || selectedLocationObj.name || selectedLocationName;
      } else if (rawLocations.length > 0) {
        selectedLocationId = Number(rawLocations[0].Auto ?? rawLocations[0].auto ?? 0);
        selectedLocationName = rawLocations[0].Name || rawLocations[0].name || selectedLocationName;
      }

      if (!selectedLocationId && activeDbCode) {
        try {
          const locRes = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${activeDbCode}`);
          const locRecs = (locRes.data || []).filter(
            (r) => String(r.Type || r.type || "").toLowerCase() === "location"
          );
          const matched = locRecs.find(
            (b) => (b.Name || b.name || "").trim().toLowerCase() === (loginData.location || "").trim().toLowerCase()
          ) || locRecs[0];
          if (matched) {
            selectedLocationId = Number(matched.Auto ?? matched.auto ?? 0);
            selectedLocationName = matched.Name || matched.name || selectedLocationName;
          }
        } catch (_) {}
      }

      sessionStorage.setItem("authToken", token);
      sessionStorage.setItem("agentCode", agentCode);
      sessionStorage.setItem("Uaccno", loginData.password);
      sessionStorage.setItem("userId", userID);
      sessionStorage.setItem("userRegisterId", userRegisterId);
      sessionStorage.setItem("userType", userType);
      sessionStorage.setItem("dbCode", activeDbCode);
      sessionStorage.setItem("loginLocation", selectedLocationName);
      sessionStorage.setItem("loginLocationId", selectedLocationId);
      sessionStorage.setItem("loginDate", loginData.loginDate);

      localStorage.setItem("authToken", token);
      localStorage.setItem("agentCode", agentCode);
      localStorage.setItem("Uaccno", loginData.password);
      localStorage.setItem("userId", userID);
      localStorage.setItem("userRegisterId", userRegisterId);
      localStorage.setItem("userType", userType);
      localStorage.setItem("dbCode", activeDbCode);
      localStorage.setItem("loginLocation", selectedLocationName);
      localStorage.setItem("loginLocationId", selectedLocationId);
      localStorage.setItem("loginDate", loginData.loginDate);
      localStorage.setItem("CustomerName", customer);
      localStorage.setItem("uRights", JSON.stringify(uRights));

      sessionStorage.removeItem("userSelectedFy");
      localStorage.removeItem("userSelectedFy");

      setDbCode(activeDbCode);
      dispatch(setActiveLocation({ id: selectedLocationId, name: selectedLocationName }));
      dispatch(fetchActiveFinancialYear(activeDbCode));
      login(agentCode, userType, activeDbCode, uRights, userID, userRegisterId);

      if (response.status === 200) {
        try {
          const menuResponse = await axios.get(
            `${apiBaseUrl}/common/getMenuTree/${activeDbCode}/${response.data.userId}`
          );
          if (menuResponse.data.success) {
            const menuTree = menuResponse.data.tree;
            const flatMap = flattenMenuTree(menuTree);
            dispatch(setMenuPermissions(flatMap));
          }
        } catch (err) {
          console.error("Error fetching menu tree:", err);
        }
      }

      try {
        const settingsResponse = await axios.get(
          `${apiBaseUrl}/common/getSettings/${activeDbCode}`
        );
        if (settingsResponse.data.success) {
          dispatch(setSoftwareSettings(settingsResponse.data.data));
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
      }

      navigate("/home");
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) {
          setError("Invalid username or password");
        } else {
          setError("An error occurred during login.");
        }
      } else if (err.request) {
        setError("No response from server. Check network connection.");
      } else {
        setError("Error setting up login request.");
      }
      console.error("Login Error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.history.pushState(null, null, window.location.pathname);
    const handlePopstate = () => {
      window.history.pushState(null, null, window.location.pathname);
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, []);

  const handleExit = () => {
    const confirmExit = window.confirm("Are you sure you want to exit?");
    if (confirmExit) {
      localStorage.removeItem("dbCode");
      logout();
      navigate("/");
    }
  };

  const inputClass =
    "w-full pl-8 pr-2.5 py-0.5 text-xs font-semibold text-slate-800 bg-slate-50/80 border border-slate-200 rounded-md focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-1.5 focus:ring-blue-500/20 font-['Inter'] transition-all duration-200 shadow-2xs placeholder:text-slate-400 placeholder:font-medium h-7";

  return (
    <div
      className="login-page-bg min-h-screen w-full flex items-center justify-center p-2 font-['Inter'] select-none relative"
    >
      {/* Overlay Dimming */}
      <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-[1px]" />

      {/* Super Compact Ultra-Sleek Glassmorphic Login Card */}
      <div className="relative z-10 w-[310px] max-w-[95vw] bg-white/95 backdrop-blur-xl rounded-xl shadow-[0_15px_45px_rgba(0,0,0,0.22)] p-4 flex flex-col items-center font-['Inter'] border border-white/80">
        
        {/* Brand Header & Logo */}
        <div className="flex items-center justify-center gap-1.5 mb-0.5">
          <div className="p-1 bg-blue-50/80 rounded-lg border border-blue-100/80 flex items-center justify-center">
            <img src={ss} alt="SherSoft Logo" className="h-4.5 w-auto object-contain" />
          </div>
          <span className="text-base font-black text-slate-900 tracking-tight font-['Inter']">
            SherGold <span className="text-blue-600 font-extrabold">ERP</span>
          </span>
        </div>

        {/* Database Pill Badge */}
        <div className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-slate-100/90 rounded-full border border-slate-200/80 mb-3 mt-0.5">
          <svg className="w-2.5 h-2.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
          <span className="text-[10px] font-extrabold text-slate-600 font-['Inter'] tracking-wider uppercase">
            {activeDbCode ? activeDbCode : "TEST"}
          </span>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="w-full bg-red-50/90 border border-red-200 p-1.5 rounded-md mb-2 text-[10px] text-red-600 font-semibold flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="leading-tight">{error}</span>
          </div>
        )}

        {/* Form Body */}
        <div className="w-full space-y-1.5 font-['Inter']">
          
          {/* 1. User Name */}
          <div>
            <label className="text-[10px] font-bold text-slate-700 block font-['Inter'] mb-0.5">
              User Name
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-2.5 text-slate-400 pointer-events-none">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
              <input
                type="text"
                ref={userNameRef}
                value={loginData.userName}
                onChange={(e) => setLoginData({ ...loginData, userName: e.target.value })}
                onKeyDown={(e) => handleKeyDown(e, passwordRef)}
                placeholder="Enter Username"
                className={inputClass}
              />
            </div>
          </div>

          {/* 2. Password */}
          <div>
            <label className="text-[10px] font-bold text-slate-700 block font-['Inter'] mb-0.5">
              Password
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-2.5 text-slate-400 pointer-events-none">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              <input
                type={showPassword ? "text" : "password"}
                ref={passwordRef}
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                onKeyDown={(e) => handleKeyDown(e, locationRef)}
                placeholder="Enter Password"
                className={`${inputClass} pr-8`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.025 10.025 0 014.122-.863c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m-6.09-2.036a3 3 0 104.243-4.243m-4.243 4.243L3 3l18 18" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* 3. Location */}
          <div>
            <label className="text-[10px] font-bold text-slate-700 block font-['Inter'] mb-0.5">
              Location
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-2.5 text-slate-400 pointer-events-none">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0h4m-4 0V11m0 0h4m-4 0H7" />
                </svg>
              </span>
              <select
                ref={locationRef}
                value={loginData.location}
                onChange={(e) => setLoginData({ ...loginData, location: e.target.value })}
                onKeyDown={(e) => handleKeyDown(e, dateRef)}
                className={`${inputClass} appearance-none cursor-pointer pr-7`}
              >
                {locationOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <span className="absolute right-2.5 pointer-events-none text-slate-400">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>
          </div>

          {/* 4. Date */}
          <div>
            <label className="text-[10px] font-bold text-slate-700 block font-['Inter'] mb-0.5">
              Date
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-2.5 text-slate-400 pointer-events-none">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </span>
              <input
                type="date"
                ref={dateRef}
                value={loginData.loginDate}
                onChange={(e) => setLoginData({ ...loginData, loginDate: e.target.value })}
                onKeyDown={(e) => handleKeyDown(e, loginBtnRef)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Buttons Row */}
          <div className="flex gap-2 pt-1.5">
            <button
              type="button"
              ref={loginBtnRef}
              onClick={handleLogin}
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] text-white font-extrabold rounded-md shadow-xs text-xs font-['Inter'] cursor-pointer flex items-center justify-center transition-all h-9 border-none disabled:opacity-60"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Authenticating...</span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span>Log In</span>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              )}
            </button>

            <button
              type="button"
              onClick={handleExit}
              className="flex-1 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-md border border-slate-200 text-xs font-['Inter'] cursor-pointer flex items-center justify-center transition-all h-9 hover:border-slate-300"
            >
              <span>Cancel</span>
            </button>
          </div>

        </div>

        {/* Footer Info */}
        <div className="mt-2.5 pt-2 border-t border-slate-100 w-full text-center flex flex-col items-center gap-0.5">
          <div className="flex items-center gap-1 text-[9px] text-slate-400 font-semibold font-['Inter']">
            <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>256-Bit SSL Encrypted Enterprise Gateway</span>
          </div>
          <p className="text-[9px] text-slate-400 font-medium font-['Inter']">
            © SherSoft Software Company
          </p>
        </div>

      </div>
    </div>
  );
}

export default LoginPage;
